import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  TableSortLabel,
} from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material'
import { AreaFuncional, Empresa, Grupo } from '../../types/api'

interface AreasFuncionaisTableProps {
  areas: AreaFuncional[]
  grupos: Grupo[]
  empresas?: Empresa[]
  onEdit: (area: AreaFuncional) => void
  onDelete: (area: AreaFuncional) => void
  loading?: boolean
}

const AreasFuncionaisTable: React.FC<AreasFuncionaisTableProps> = ({
  areas = [],
  grupos = [],
  empresas = [],
  onEdit,
  onDelete,
  loading,
}) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<keyof AreaFuncional | 'grupo_nome' | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const safeAreas = Array.isArray(areas) ? areas : []
  
  // Função para obter nome do grupo
  const getGrupoNome = (grupoId: number): string => {
    const grupo = grupos.find((g) => g.id === grupoId)
    return grupo?.nome || `Grupo ${grupoId}`
  }

  // Empresas que compartilham o mesmo conjunto de áreas (a entidade é do grupo)
  const getEmpresasDoGrupo = (grupoId: number): string => {
    const nomes = empresas
      .filter((empresa) => empresa.grupo_id === grupoId)
      .map((empresa) => empresa.nome)
    if (nomes.length === 0) return '-'
    return `${nomes.length}: ${nomes.join(', ')}`
  }

  const filteredAreas = useMemo(
    () =>
      safeAreas.filter(
        (area) =>
          area.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (area.descricao && area.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
      ),
    [safeAreas, searchTerm]
  )

  const sortedAreas = useMemo(() => {
    if (!sortField || !sortDirection) {
      return filteredAreas
    }

    return [...filteredAreas].sort((a, b) => {
      let aValue: any
      let bValue: any

      if (sortField === 'grupo_nome') {
        aValue = getGrupoNome(a.grupo_id)
        bValue = getGrupoNome(b.grupo_id)
      } else {
        aValue = a[sortField]
        bValue = b[sortField]
      }

      if (aValue == null && bValue == null) return 0
      if (aValue == null) return sortDirection === 'asc' ? -1 : 1
      if (bValue == null) return sortDirection === 'asc' ? 1 : -1

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
      }

      const comparison = String(aValue).localeCompare(String(bValue), undefined, {
        sensitivity: 'base',
        numeric: true,
      })

      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [filteredAreas, sortField, sortDirection, grupos])

  const paginatedAreas = useMemo(() => {
    const start = page * rowsPerPage
    return sortedAreas.slice(start, start + rowsPerPage)
  }, [sortedAreas, page, rowsPerPage])

  const handleSort = (field: keyof AreaFuncional | 'grupo_nome') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
    setPage(0)
  }

  if (loading) {
    return (
      <Box p={3}>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    )
  }

  return (
    <Paper>
      <Box p={2}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={t('common.search', 'Buscar...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiInputBase-root': { minHeight: 28 },
            '& .MuiOutlinedInput-input': { py: 0.75 },
          }}
        />
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'id'}
                  direction={sortField === 'id' ? sortDirection : 'asc'}
                  onClick={() => handleSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'grupo_nome'}
                  direction={sortField === 'grupo_nome' ? sortDirection : 'asc'}
                  onClick={() => handleSort('grupo_nome')}
                >
                  Grupo
                </TableSortLabel>
              </TableCell>
              <TableCell>Empresas do grupo</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'nome'}
                  direction={sortField === 'nome' ? sortDirection : 'asc'}
                  onClick={() => handleSort('nome')}
                >
                  Nome
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'descricao'}
                  direction={sortField === 'descricao' ? sortDirection : 'asc'}
                  onClick={() => handleSort('descricao')}
                >
                  Descrição
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'iduser'}
                  direction={sortField === 'iduser' ? sortDirection : 'asc'}
                  onClick={() => handleSort('iduser')}
                >
                  ID User
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {safeAreas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t('common.noData', 'Nenhum dado encontrado')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedAreas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Nenhum resultado encontrado para a busca
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedAreas.map((area) => (
                <TableRow key={area.id} hover>
                  <TableCell>{area.id}</TableCell>
                  <TableCell>{getGrupoNome(area.grupo_id)}</TableCell>
                  <TableCell>{getEmpresasDoGrupo(area.grupo_id)}</TableCell>
                  <TableCell>{area.nome}</TableCell>
                  <TableCell>{area.descricao || '-'}</TableCell>
                  <TableCell>{area.iduser || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => onEdit(area)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => onDelete(area)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sortedAreas.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10))
          setPage(0)
        }}
        labelRowsPerPage={t('common.rowsPerPage', 'Linhas por página')}
      />
    </Paper>
  )
}

export default AreasFuncionaisTable

